// notificationsSlice — NOTIFY-F002 (docs/TRD/task-notifications-contract.md §7.2, source TRD
// §2.4). State shape is the seven fields §2.4 lists by name (`unreadCount`, `items`,
// `status`, `lastPolledAt`, `isPollingPaused`, `pushPermission`, `pushEnabled`), plus
// additive fields the push work needs (`hasMore` from NOTIFY-F002; `pushPublicKey` and
// `pushConfigured` from NOTIFY-F007/F008, see below) — additive, not a replacement for the
// authoritative seven.
//
// Backend holds no in-memory notification state (§2.4): every fact is a row, and this slice
// is purely a client-side cache/optimistic layer over it. The 60s poll is the reconciliation
// mechanism, so a wrong guess here self-heals within 60s regardless.
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getUnreadCount,
  getNotifications,
  markNotificationRead,
  markAllNotifications,
  getPushPublicKey,
  getNotificationPreference,
  setNotificationPreference,
} from "../../API";

const initialState = {
  unreadCount: 0,
  items: [],
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  lastPolledAt: null,
  isPollingPaused: false,
  // P1 — 'default' | 'granted' | 'denied', or `null` while still unknown. Review fix
  // (L5, I1/I2): defaults to `null` (not 'default') so PushSettings can distinguish "not
  // checked yet" from the real 'default' permission state and render nothing until its
  // mount effect resolves the live `Notification.permission` — a flashed Enable button is
  // worse than a half-second of nothing (AC-F6.8's "never flash" reasoning applied to this
  // gate too). There is no 'unsupported' value here — a browser without
  // serviceWorker/PushManager never reaches this field at all: PushSettings' own
  // `supported` flag (isPushSupported()) short-circuits to `return null` first.
  pushPermission: null,
  // P1 — source TRD §2.4's "server-side preference" field (AC-F10.3). NOTIFY-F008: the
  // real per-user push on/off preference, backed by `GET`/`PUT /api/notification_preference`
  // (`fetchPushPreference` / `setPushPreference` below). Defaults `true` to match the
  // backend's own default for a user with no preference row yet (NOTIFY-B013).
  pushEnabled: true,
  // Additive (NOTIFY-F007): whether push is *configured on this server at all* (VAPID keys
  // present), from `GET /api/push/public-key`'s `enabled` flag — distinct from the user's
  // own preference above. `false` means "hide the push affordance entirely" (contract §6),
  // regardless of `pushEnabled` — an unconfigured server has no preference to show either
  // way. Previously this server-configured flag was (mis)carried on `pushEnabled` itself,
  // ahead of F008; F008 splits the two so a user can turn their own preference off without
  // the control disappearing (AC-F10.4 — re-enabling must resume with no re-prompt, which
  // requires the toggle to stay visible while off).
  // Review fix (L5): `null` (unknown) until the public-key fetch resolves — the old
  // `true` default is what let PushSettings render an Enable button before the fetch had
  // even returned, so a request that silently resolves with no data (B1) still looked
  // "configured" and rendered a button wired to an undefined key.
  pushConfigured: null,
  // Additive: the VAPID public key from the public-key endpoint, needed by
  // PushSettings.js to call `pushManager.subscribe({ applicationServerKey })`.
  pushPublicKey: null,
  // Additive (code review F1) — whether THIS browser already has a live push
  // subscription. Previously local `useState` inside usePushEnable.js, which meant every
  // mount (the bell dropdown's PushSettings AND the post-login PushNudgeBanner) tracked its
  // own copy: enabling from one left the other showing stale "not subscribed" state (the
  // banner kept nagging after the bell already enabled it) and a second Enable click from
  // the other UI double-POSTed a subscribe the server already had. Centralising it here
  // gives every `usePushEnable()` instance the same value regardless of which one performed
  // the mount-time `getSubscription()` lookup or the click-time `enable()`. `null` (like
  // `pushPermission`/`pushConfigured`) means "not checked yet", not "not subscribed" —
  // callers that need a strict boolean compare against `true`.
  pushSubscribed: null,
  hasMore: false,
};

// Redux's serializable-state check (dev-only middleware) flags a raw Error/axios-error
// object in an action payload, so only a plain, serializable summary is ever passed to
// `rejectWithValue` below — never the caught error itself.
const toSerializableError = (err) => ({
  message: err?.response?.data?.message || err?.message || "Request failed",
  name: err?.response?.data?.name,
});

// Each thunk below catches its own API call with `rejectWithValue` rather than letting
// `createAsyncThunk` catch a raw throw. Both paths dispatch a `rejected` action instead of
// throwing out of `dispatch(...)`, but being explicit here keeps the "never rethrows" rule
// (AC-F4.7) visible at the call site rather than relying on an implicit RTK behaviour.
export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getUnreadCount();
      return res.data.unread_count;
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (params, { rejectWithValue }) => {
    try {
      const res = await getNotifications(params);
      // Object envelope, not a bare array (contract §3.2, D12): `{ data, limit, offset,
      // has_more }`.
      return res.data;
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  }
);

// Optimistic: the `pending` reducer below fires synchronously on dispatch, before the
// `await` inside this thunk ever resolves, so the badge decrements and `read_at` is stamped
// immediately (AC-F4.4). `rejected` reverts both. The server call is idempotent (contract
// §3.4), so a retry after a revert is safe, and the 60s poll reconciles either way.
export const markRead = createAsyncThunk(
  "notifications/markRead",
  async (id, { rejectWithValue }) => {
    try {
      const res = await markNotificationRead(id);
      return res.data; // { id, read_at }
    } catch (err) {
      return rejectWithValue({ id, ...toSerializableError(err) });
    }
  }
);

export const markAllRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const res = await markAllNotifications();
      return res.data; // { affected }
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  }
);

// NOTIFY-F007. Fetched once per session (code review F2) rather than once per mount —
// `condition` below skips the dispatch once `pushConfigured` has resolved to anything but
// `null`, so the bell's PushSettings and the post-login PushNudgeBanner mounting the same
// `usePushEnable()` hook (and PushNudgeBanner's own extra fetch for the iOS-unsupported
// case) no longer each fire their own GET. Never gated behind a click — only
// `Notification.requestPermission()` is (AC-F6.2, R11). A failure here is treated the same
// as `enabled: false`: hide the control rather than show-and-break (never 500s per D10, but
// a network failure is still possible client-side).
export const fetchPushPublicKey = createAsyncThunk(
  "notifications/fetchPushPublicKey",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getPushPublicKey();
      // Review fix (B1, R11) — `baseApi.js`'s default branch RESOLVES (doesn't reject) on
      // a 5xx or network error, so `res` can come back `undefined` or with no `.data` here
      // even though this `try` never threw. Without this check the thunk "fulfills" with
      // `undefined`, `pushConfigured` stays truthy in the fulfilled reducer below, and
      // PushSettings renders an Enable button wired to a public key that was never
      // fetched — which then burns the user's one-shot permission prompt and crashes in
      // `urlBase64ToUint8Array(null)`. Treated identically to a caught rejection: hide the
      // control (contract §6's "enabled: false ... hide all push UI" failure mode).
      if (res?.status !== 200 || !res?.data) {
        return rejectWithValue({ message: "Request failed" });
      }
      return res.data; // { public_key, enabled }
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  },
  {
    // Skip the dispatch once a previous fetch has already resolved either way (fulfilled ->
    // `true`/`false`, rejected -> `false`) — only the unresolved `null` default still needs
    // the request. `getState` is the pre-dispatch state, so a `pending` fetch already in
    // flight also reads as `null` here and is left alone (RTK's own `condition` semantics
    // only block a *second* `dispatch` call, not the in-flight one).
    condition: (_, { getState }) => getState().notifications.pushConfigured === null,
  }
);

// NOTIFY-F008 (contract §6, AC-F10.3). Fetched once by PushSettings when its 'granted'
// branch mounts — the toggle only makes sense once a subscription exists to turn off/on. A
// failure here is left as a status flag; `pushEnabled` keeps its current/default value
// rather than being forced off, since defaulting to "on" is the safer failure mode (a
// dropped GET shouldn't silently disable something the user already turned on).
export const fetchPushPreference = createAsyncThunk(
  "notifications/fetchPushPreference",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getNotificationPreference();
      return res.data.push_enabled;
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  }
);

// NOTIFY-F008 (AC-F10.4, AC-F10.5). Optimistic: the `pending` reducer flips `pushEnabled` to
// the requested value synchronously, before the PUT resolves, so the switch responds
// immediately. On failure it reverts. This never unsubscribes the browser or deletes the
// server subscription (AC-F10.4) — it only ever calls the preference endpoint.
export const setPushPreference = createAsyncThunk(
  "notifications/setPushPreference",
  async (pushEnabled, { rejectWithValue }) => {
    try {
      const res = await setNotificationPreference({ push_enabled: pushEnabled });
      return res.data.push_enabled;
    } catch (err) {
      return rejectWithValue(toSerializableError(err));
    }
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setPollingPaused: (state, action) => {
      state.isPollingPaused = action.payload;
    },
    setLastPolledAt: (state, action) => {
      state.lastPolledAt = action.payload;
    },
    // NOTIFY-F007. `Notification.permission` lives on the browser, not the server, so this
    // is a plain reducer (synced from the DOM by PushSettings.js) rather than a thunk.
    setPushPermission: (state, action) => {
      state.pushPermission = action.payload;
    },
    // Code review F1 — shared replacement for usePushEnable's old per-instance `subscribed`
    // local state; see the `pushSubscribed` field comment above.
    setPushSubscribed: (state, action) => {
      state.pushSubscribed = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.unreadCount = action.payload;
        // Stamped on every successful poll (not just the interval tick) so the
        // component's visibility-resume handler can debounce rapid tab switching
        // against a real "last known good" timestamp (TR9).
        state.lastPolledAt = Date.now();
      })
      .addCase(fetchUnreadCount.rejected, (state) => {
        // Neutral bell, never an exception (AC-F4.7) — unreadCount is left as-is; the
        // next successful poll reconciles it.
        state.status = "failed";
      })

      .addCase(fetchNotifications.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload.data;
        state.hasMore = action.payload.has_more;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(markRead.pending, (state, action) => {
        const id = action.meta.arg;
        const item = state.items.find((notification) => notification.id === id);
        if (item && !item.read_at) {
          item.read_at = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markRead.fulfilled, (state, action) => {
        // Reconcile the optimistic stamp with the server's actual read_at (mark-read is
        // idempotent server-side — contract §3.4 — so this is always the true value).
        const { id, read_at } = action.payload;
        const item = state.items.find((notification) => notification.id === id);
        if (item) {
          item.read_at = read_at;
        }
      })
      .addCase(markRead.rejected, (state, action) => {
        const { id } = action.payload || {};
        const item = state.items.find((notification) => notification.id === id);
        if (item) {
          item.read_at = null;
          state.unreadCount += 1;
        }
      })

      .addCase(markAllRead.fulfilled, (state) => {
        const now = new Date().toISOString();
        state.unreadCount = 0;
        state.items = state.items.map((item) => ({
          ...item,
          read_at: item.read_at || now,
        }));
      })
      .addCase(markAllRead.rejected, (state) => {
        // Never throws out of dispatch (AC-F4.7) — the failed request already left
        // unreadCount/items untouched (no optimistic update on this thunk), so a
        // status flag is the only correction needed here.
        state.status = "failed";
      })

      .addCase(fetchPushPublicKey.fulfilled, (state, action) => {
        state.pushConfigured = action.payload.enabled;
        state.pushPublicKey = action.payload.public_key;
      })
      .addCase(fetchPushPublicKey.rejected, (state) => {
        // Show-and-break is worse than hidden (contract §6) — treat a failed fetch the
        // same as `enabled: false`. Does not touch the shared `status` field: this is not
        // the bell's own fetch/poll status (AC-F4.7 is about the bell, not push).
        state.pushConfigured = false;
        state.pushPublicKey = null;
      })

      .addCase(fetchPushPreference.fulfilled, (state, action) => {
        state.pushEnabled = action.payload;
      })
      .addCase(fetchPushPreference.rejected, (state) => {
        state.status = "failed";
      })

      .addCase(setPushPreference.pending, (state, action) => {
        state.pushEnabled = action.meta.arg;
      })
      .addCase(setPushPreference.fulfilled, (state, action) => {
        state.pushEnabled = action.payload;
      })
      .addCase(setPushPreference.rejected, (state, action) => {
        // Binary toggle, so the value immediately before this optimistic dispatch is
        // always the negation of what we just set (`action.meta.arg`) — no separate
        // "previous value" field is needed to revert correctly.
        state.pushEnabled = !action.meta.arg;
        state.status = "failed";
      });
  },
});

export const { setPollingPaused, setLastPolledAt, setPushPermission, setPushSubscribed } =
  notificationsSlice.actions;
export default notificationsSlice.reducer;
